/**
 * WhiteboardPresenter: ExcalidrawApp 래퍼 + UI 오버레이
 * v1 원본: utils/whiteboard/WhiteboardPresenter.js (450줄)
 * v2 변환: TypeScript 함수형 컴포넌트
 *
 * ExcalidrawApp은 @jitsi/excalidraw 패키지에서 가져옴 (지연 로딩)
 * 아직 설치되지 않은 경우 플레이스홀더 렌더링
 */
import React, { useCallback, useState } from 'react';
import { exportToBlob } from '@jitsi/excalidraw';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { whiteboardActions } from '@/store/slices/whiteboardSlice';
import { COLLAB_SERVER_URL, DOC_SHARE_BACKGROUND } from '@/utils/whiteboard/constants';
import { stopWhiteboardSession } from '@/utils/whiteboard';
import { uploadFiles } from '@/utils/objectStorage';
import apiManager from '@/api';
import apiConfig from '@/api/config';
import {
  PageInfo,
  DrawNavigation,
  PageNavigation,
  SketchModeTitle,
  SketchExpandButton,
  DocShareHeader,
  DocShareSidebar,
  DocShareDrawTools,
} from './WhiteboardUIComponents';
import type { ExcalidrawAPI } from '@/hooks/useWhiteboardCommon';
import LUXDialog from '@/components/Dialog';
import '@jitsi/excalidraw/index.css';
import './RenewalSchech.css';
import { ExcalidrawApp } from '@jitsi/excalidraw';
import { toastActions } from '@/store/slices/toastSlice';

interface WhiteboardPresenterProps {
  // 상태
  isValid: boolean;
  isLoadingPage: boolean;
  isFilesLoading: boolean;
  setIsFilesLoading: (v: boolean) => void;
  isViewOnly: boolean;
  isSessionOwner: boolean;
  isDocShareMode: boolean;
  isFullscreen: boolean;
  isSubVideoMode: boolean;
  pdfFileName: string;
  drawFileList: unknown[];
  docShareFiles: Array<{ thumbnailURL: string }>;
  editPermissions?: unknown;
  participants?: Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number; canEdit?: boolean }>;

  wrapperHeight: string;

  // refs
  excalidrawApi: React.RefObject<ExcalidrawAPI | null>;
  whiteboardRef: React.RefObject<HTMLDivElement | null>;

  // 페이지 데이터
  roomId: string | null;
  roomKey: string | null;
  totalPages: number;
  currentPage: number;

  // 도구
  currentTool: string;
  setCurrentTool: (tool: string) => void;
  isOpenDiagramPopover: boolean;
  setIsOpenDiagramPopover: (v: boolean) => void;
  onClickTools: (type: string) => void;

  // 줌
  zoomNumber: number;
  setZoomNumber: (zoom: number) => void;
  onClickZoomIn: () => void;
  onClickZoomOut: () => void;

  // 액션
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handlePageChange: (page: number) => void;
  handleToggleEditorMode: (isAllEditable: boolean) => void;
  handleGetExcalidrawAPI: (api: ExcalidrawAPI) => void;
  handleGetCollabAPI: (api: { setUsername: (name: string) => void }) => void;
  onChangeParticipants: (p: Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number }>) => void;
}

const WhiteboardPresenter: React.FC<WhiteboardPresenterProps> = ({
  isValid,
  isLoadingPage,
  isFilesLoading,
  setIsFilesLoading,
  isViewOnly,
  isSessionOwner,
  isDocShareMode,
  isFullscreen,
  isSubVideoMode,
  pdfFileName,
  drawFileList,
  docShareFiles,
  editPermissions,
  participants,
  wrapperHeight,
  excalidrawApi,
  whiteboardRef,
  roomId,
  roomKey,
  totalPages,
  currentPage,
  currentTool,
  setCurrentTool,
  isOpenDiagramPopover,
  setIsOpenDiagramPopover,
  onClickTools,
  zoomNumber,
  setZoomNumber,
  onClickZoomIn,
  onClickZoomOut,
  handlePrevPage,
  handleNextPage,
  handlePageChange,
  handleToggleEditorMode,
  handleGetExcalidrawAPI,
  handleGetCollabAPI,
  onChangeParticipants,
}) => {
  const dispatch = useAppDispatch();
  const whiteboard = useAppSelector((s) => s.whiteboard);
  const local = useAppSelector((s) => s.local);

  // 표시 페이지 (1-indexed)
  const displayPage = currentPage + 1;

  // 캡쳐 (PNG 다운로드 + 공유자인 경우 회의자료 업로드)
  const handleCapture = useCallback(async () => {
    const api = excalidrawApi.current;
    if (!api) return;

    try {
      const elements = api.getSceneElements();
      const appState = api.getAppState();
      const files = api.getFiles();

      const blob = await exportToBlob({
        elements,
        appState: {
          ...appState,
          exportWithDarkMode: false,
          exportBackground: true,
        },
        files,
      });

      // 파일명: 스케치_yyyy_mm_dd_HHmmss
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const dateStr = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const fileName = `스케치_${dateStr}.png`;

      // 파일 다운로드 트리거
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      dispatch(toastActions.showSnackbar({ type: 'success', message: '현재 스케치 화면이 캡쳐되었습니다.'}));

      // 공유자(세션 오너)인 경우 스케치로 저장
      if (isSessionOwner && local.roomId) {
        const file = new File([blob], fileName, { type: 'image/png' });
        const uploadResult = await uploadFiles(file);
        if (uploadResult) {
          await apiManager.createSketch(local.roomId, fileName, {
            fileUuid: uploadResult.newFileName,
            fileName: uploadResult.orgFileName,
            fileSize: uploadResult.fileSize,
            url: `${apiConfig.thumbnailBaseUrl}${uploadResult.thumbnail}`,
          });
          console.log('스케치 캡쳐가 저장되었습니다.');
        }
      }
    } catch (err) {
      console.error('캡쳐 실패:', err);
    }
  }, [excalidrawApi, isSessionOwner, local.roomId]);

  // 문서공유 종료 확인
  const handleCloseDocShare = useCallback(() => {
    LUXDialog.confirm('문서공유를 종료하시겠습니까?', {
      type: 'warning',
      useRenewalStyle: true,
      confirmCallback: () => {
        stopWhiteboardSession();
      },
    });
  }, []);

  // 배경색/격자 변경
  const handleChangeBgColor = useCallback((color: string) => {
    dispatch(whiteboardActions.setBackgroundProps({ viewBackgroundColor: color }));
    excalidrawApi.current?.updateScene({
      appState: { viewBackgroundColor: color },
    });
  }, [dispatch, excalidrawApi]);

  const handleChangeGrid = useCallback((gridModeEnabled: boolean, gridSize: number) => {
    dispatch(whiteboardActions.setBackgroundProps({ gridModeEnabled, gridSize }));
    excalidrawApi.current?.updateScene({
      appState: { gridModeEnabled, gridSize },
    });
  }, [dispatch, excalidrawApi]);

  // 문서공유 전용: 사이드바 숨기기 / 그리기 토글
  const [isSideHidden, setIsSideHidden] = useState(false);
  const [isSketchEnabled, setIsSketchEnabled] = useState(true);

  const handleToggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      (document.getElementById('fileViewer') ?? document.documentElement).requestFullscreen?.();
    }
  }, []);

  if (!isValid) return null;

  // ─── ExcalidrawApp 공통 렌더링 ─────────────────────────────────────────
  const excalidrawElement = ExcalidrawApp ? (
    <ExcalidrawApp
      key={`page-ExcalidrawApp-${roomId}-${currentPage}`}
      collabServerUrl={COLLAB_SERVER_URL}
      localParticipantName={local.userName || local.nickname || local.userId || local.wehagoId || ''}
      onChangeActiveTool={(ct: { type: string }) => setCurrentTool(ct.type)}
      jitsiId={(window as any)._jitsiRoom?.myUserId?.() ?? ''}
      conferenceId={(window as any)._jitsiRoom?.getName?.() ?? ''}
      collabDetails={{ roomId, roomKey }}
      storageBackendUrl={`${COLLAB_SERVER_URL}/api`}
      meetingDetails={{
        sessionId: roomId,
        roomJid: roomId,
        jwt: '',
        jid: (window as any)._jitsiRoom?.myUserId?.() ?? '',
        token: 'dummy-token',
      }}
      onChangeParticipants={(p: Array<{ isOwner?: boolean; jitsiId?: string; roomIndex?: number }>) => onChangeParticipants(p)}
      onZoomChange={(newZoom: number) => setZoomNumber(newZoom)}
      excalidraw={{
        initialZoom: isSubVideoMode ? undefined : zoomNumber,
        isCollaborating: true,
        langCode: 'ko-KR',
        initialData: {
          appState: isDocShareMode
            ? { ...DOC_SHARE_BACKGROUND }
            : {
                viewBackgroundColor: whiteboard.backgroundProps.viewBackgroundColor,
                gridModeEnabled: whiteboard.backgroundProps.gridModeEnabled,
                gridSize: whiteboard.backgroundProps.gridSize,
              },
        },
        UIOptions: {
          welcomeScreen: false,
          canvasActions: {
            allowedShapes: [],
            hideWelcomeScreen: true,
            hideHelpDialog: true,
            disableAlignItems: true,
            disableHints: true,
            disableGrouping: true,
            disableLink: true,
            disableShortcuts: true,
            disableVerticalAlignOptions: true,
            hideFontFamily: true,
            hideUserList: true,
            hideLockButton: true,
            hideTextAlign: true,
            hideArrowHeadsOptions: true,
            hideColorInput: true,
            hideEmbedableTools: true,
            hideIOActions: true,
            hideLaserOnCollaboration: true,
            hideLayers: true,
            hideLibraries: true,
            hideOpacityInput: true,
            hideStrokeStyle: true,
            loadScene: false,
            saveToActiveFile: false,
            toggleTheme: false,
            saveAsImage: false,
          },
        },
        viewModeEnabled: isViewOnly,
        zenModeEnabled: isViewOnly,
      }}
      autoFocus={false}
      autoResize={true}
      getExcalidrawAPI={handleGetExcalidrawAPI}
      getCollabAPI={handleGetCollabAPI}
    />
  ) : null;

  const viewOnlyStyles = isViewOnly ? (
    <style>
      {`.App-bottom-bar { display: none !important; }`}
      {`.App-top-bar { display: none !important; }`}
      {`.App-toolbar-container { display: none !important; }`}
      {`.App-menu_bottom { display: none !important; }`}
    </style>
  ) : null;

  // ─── 문서공유 모드: shareViewMode 마크업 ────────────────────────────────
  if (isDocShareMode) {
    return (
      <div
        id="fileViewer"
        className={`file_viewer ${isFullscreen ? 'fullmode' : ''}`}
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        {/* 로딩 스피너 */}
        {isFilesLoading && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)', zIndex: 20,
          }}>
            <style>
              {`.excalidraw__canvas{background: #fff !important;}`}
              </style>
            <div style={{ color: '#fff' }}>문서를 불러오는 중입니다...</div>
          </div>
        )}
        <style>
          {`.Island.App-menu__left { left:10px !important; }`}
        </style>


        <div className={`shareViewMode ${isSideHidden ? 'sideHide' : ''}`}>
          {/* 상단 헤더 */}
          {!isSubVideoMode && (
          <DocShareHeader
            currentPage={displayPage}
            totalPages={totalPages}
            pdfFileName={pdfFileName}
            isOwner={isSessionOwner}
            isViewOnly={isViewOnly}
            isSideHidden={isSideHidden}
            isSketchEnabled={isSketchEnabled}
            onClickPrev={handlePrevPage}
            onClickNext={handleNextPage}
            onToggleSideHide={() => setIsSideHidden(!isSideHidden)}
            onClickZoomIn={onClickZoomIn}
            onClickZoomOut={onClickZoomOut}
            onClickFullscreen={handleToggleFullscreen}
            onToggleSketch={() => setIsSketchEnabled(!isSketchEnabled)}
          />
          )}

          {/* 좌측 썸네일 사이드바 */}
          {totalPages > 0 && !isSubVideoMode && (
            <DocShareSidebar
              currentPage={displayPage}
              drawFileList={docShareFiles as Array<{ thumbnailURL?: string }>}
              onClickPageNumber={(pageNum) => handlePageChange(pageNum - 1)}
            />
          )}

          {/* 메인 캔버스 */}
          <div className="shareViewMode__con" style={{padding:isSubVideoMode ? "0" : ""}}>
            <div
              id="excalidraw-wrapper"
              ref={whiteboardRef}
              className="excalidraw-wrapper whiteboardEx"
              style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
            >
              <style>
                {`.SVGLayer {
                  position: fixed !important;
                  top: 0 !important; left: 0 !important;
                  width: 100vw !important; height: 100vh !important;
                  margin-top: 0 !important;
                }`}
              </style>
              {viewOnlyStyles}
              {excalidrawElement}

              {/* 가로 드로잉 도구 (그리기 ON + 편집 가능) */}
              {isSketchEnabled && isLoadingPage && !isSubVideoMode && !isViewOnly && !isFilesLoading && (
                <DocShareDrawTools
                  currentTool={currentTool}
                  onClickTools={onClickTools}
                  isOpenDiagramPopover={isOpenDiagramPopover}
                  setIsOpenDiagramPopover={setIsOpenDiagramPopover}
                  excalidrawApi={excalidrawApi as React.RefObject<{ setActiveTool: (tool: { type: string }) => void } | null>}
                  onClickUndo={() => excalidrawApi.current?.undo()}
                  onClickRedo={() => excalidrawApi.current?.redo()}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── 스케치 모드: 기존 마크업 유지 ──────────────────────────────────────
  return (
    <div
      id="fileViewer"
      className={`file_viewer ${isFullscreen ? 'fullmode' : ''}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#f5f6f8',
      }}
    >
      {/* 로딩 스피너 */}
      {isFilesLoading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.8)',
          zIndex: 20,
        }}>
          <div>문서를 불러오는 중입니다...</div>
        </div>
      )}

      {/* Excalidraw 컨테이너 */}
      <div
        id="excalidraw-wrapper"
        ref={whiteboardRef}
        className="excalidraw-wrapper whiteboardEx"
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        <style>
          {`.SVGLayer {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            margin-top: 0 !important;
          }`}
        </style>
        {viewOnlyStyles}
        {excalidrawElement}
      </div>

      {/* 스케치 모드 설정 패널 — excalidraw-wrapper 밖: 코치마크 딤드 위에 표시되어야 함 */}
      {!isSubVideoMode && (
        <SketchModeTitle
          backgroundProps={whiteboard.backgroundProps}
          onChangeBgColor={handleChangeBgColor}
          onChangeGrid={handleChangeGrid}
          isOwner={isSessionOwner}
          editPermissions={editPermissions}
          participants={participants}
          onChangeEditorMode={handleToggleEditorMode}
        />
      )}

      {/* 우측 상단 크게보기 버튼 */}
      {!isSubVideoMode && (
        <SketchExpandButton onClickExpand={handleToggleFullscreen} />
      )}

      {/* 왼쪽 드로잉 도구 — excalidraw-wrapper 밖: 코치마크 딤드 위에 표시되어야 함 */}
      {isLoadingPage && !isSubVideoMode && !isViewOnly && (
        <DrawNavigation
          currentTool={currentTool}
          onClickTools={onClickTools}
          isOpenDiagramPopover={isOpenDiagramPopover}
          setIsOpenDiagramPopover={setIsOpenDiagramPopover}
          excalidrawApi={excalidrawApi as React.RefObject<{ setActiveTool: (tool: { type: string }) => void } | null>}
          onClickUndo={() => excalidrawApi.current?.undo()}
          onClickRedo={() => excalidrawApi.current?.redo()}
        />
      )}

      {/* 줌 컨트롤 — excalidraw-wrapper 밖: 코치마크 딤드 위에 표시되어야 함 */}
      {isLoadingPage && !isSubVideoMode && (
        <div className="schechMode__viewBtn" style={{zIndex : 11}}>
          <div className="schechMode__viewBtnItem">
            <div className="schechMode__viewBtnSize">
              <button type="button" className="viewBtnItem viewBtnItem--zoomout" onClick={onClickZoomOut}>
                <span className="blind">축소</span>
              </button>
              <span className="viewBtnSize__text">{Math.round(zoomNumber * 100)}%</span>
              <button type="button" className="viewBtnItem viewBtnItem--zoomin" onClick={onClickZoomIn}>
                <span className="blind">확대</span>
              </button>
            </div>
            <span className="bar"></span>
            <div className="rtc__tooltipWrap">
              <button type="button" className="viewBtnItem viewBtnItem--capture" onClick={handleCapture}><span className="blind">캡쳐</span></button>
              <div className="rtc__tooltip" style={{right:"-16px"}}>
                <span className="rtc__tooltipArrow" style={{left:"auto",right:"28px"}}></span>
                <p>{isSessionOwner ? "회의기록에 저장할 스케치 화면을 ‘캡쳐’ 해보세요!" : "스케치 화면을 ‘캡쳐’ 해서 저장해 보세요!"}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhiteboardPresenter;
